import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';

// --- ICONS (using inline SVGs for portability) ---
const Icons = {
	menu: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
	),
	x: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
	),
	cpu: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><rect x="9" y="9" width="6" height="6"></rect><line x1="9" y1="1" x2="9" y2="4"></line><line x1="15" y1="1" x2="15" y2="4"></line><line x1="9" y1="20" x2="9" y2="23"></line><line x1="15" y1="20" x2="15" y2="23"></line><line x1="20" y1="9" x2="23" y2="9"></line><line x1="20" y1="14" x2="23" y2="14"></line><line x1="1" y1="9" x2="4" y2="9"></line><line x1="1" y1="14" x2="4" y2="14"></line></svg>
	),
	shield: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
	),
	arrowRight: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
	),
	check: (props) => (
		 <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
	),
	messageSquare: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
	),
	send: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
	),
	zap: (props) => (
		<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
	),
};

// --- DYNAMIC HERO BACKGROUND ---
const HeroCanvas = () => {
	const canvasRef = useRef(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		let animationFrameId;

		let width, height;
		const particles = [];
		const particleCount = 60;
		const maxDistance = 120;

		const resizeCanvas = () => {
			width = canvas.parentElement.offsetWidth;
			height = canvas.parentElement.offsetHeight;
			canvas.width = width;
			canvas.height = height;
		};

		class Particle {
			constructor() {
				this.x = Math.random() * width;
				this.y = Math.random() * height;
				this.vx = Math.random() * 0.5 - 0.25;
				this.vy = Math.random() * 0.5 - 0.25;
				this.radius = Math.random() * 2 + 1;
			}

			update() {
				this.x += this.vx;
				this.y += this.vy;

				if (this.x < 0 || this.x > width) this.vx *= -1;
				if (this.y < 0 || this.y > height) this.vy *= -1;
			}

			draw() {
				ctx.beginPath();
				ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
				ctx.fillStyle = 'rgba(192, 132, 252, 0.5)';
				ctx.fill();
			}
		}

		const init = () => {
			resizeCanvas();
			particles.length = 0;
			for (let i = 0; i < particleCount; i++) {
				particles.push(new Particle());
			}
		};

		const connectParticles = () => {
			for (let i = 0; i < particles.length; i++) {
				for (let j = i + 1; j < particles.length; j++) {
					const dx = particles[i].x - particles[j].x;
					const dy = particles[i].y - particles[j].y;
					const distance = Math.sqrt(dx * dx + dy * dy);

					if (distance < maxDistance) {
						const opacity = 1 - (distance / maxDistance);
						ctx.beginPath();
						ctx.moveTo(particles[i].x, particles[i].y);
						ctx.lineTo(particles[j].x, particles[j].y);
						ctx.strokeStyle = `rgba(167, 139, 250, ${opacity})`;
						ctx.lineWidth = 0.5;
						ctx.stroke();
					}
				}
			}
		};

		const animate = () => {
			ctx.clearRect(0, 0, width, height);
			particles.forEach(p => p.update());
			particles.forEach(p => p.draw());
			connectParticles();
			animationFrameId = requestAnimationFrame(animate);
		};

		init();
		animate();

		window.addEventListener('resize', init);

		return () => {
			window.removeEventListener('resize', init);
			cancelAnimationFrame(animationFrameId);
		};
	}, []);

	return <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full z-0 opacity-50" />;
};


// --- PAGE COMPONENTS ---

const Home = ({ setPage }) => {
	// Data for charts
	const deploymentData = [
		{ name: '2024', agents: 100 }, { name: '2025', agents: 500 },
		{ name: '2026', agents: 2500 }, { name: '2027', agents: 9000 },
		{ name: '2028', agents: 25000 }, { name: '2029', agents: 60000 },
		{ name: '2030', agents: 150000 },
	];
	
	const automationData = [{ name: 'AI Handled', value: 60 }, { name: 'Human Strategy', value: 40 }];
	const PIE_COLORS = ['#8884d8', '#82ca9d'];

	// Partners logos
	const partners = [
		"https://placehold.co/150x50/111827/ffffff?text=Mollie",
		"https://placehold.co/150x50/111827/ffffff?text=BarclayCard",
		"https://placehold.co/150x50/111827/ffffff?text=Dotdigital",
		"https://placehold.co/150x50/111827/ffffff?text=Adobe+Commerce",
		"https://placehold.co/150x50/111827/ffffff?text=Shopify",
		"https://placehold.co/150x50/111827/ffffff?text=BigCommerce",
		"https://placehold.co/150x50/111827/ffffff?text=Mailchimp",
		"https://placehold.co/150x50/111827/ffffff?text=Gorgias",
	];
	
	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
			{/* Hero Section */}
			<div className="relative bg-gray-900 text-white overflow-hidden">
				<HeroCanvas />
				<div className="relative z-10 min-h-screen flex items-center justify-center text-center px-4">
					<div className="max-w-4xl">
						<motion.h1 
							initial={{ y: 20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400 mb-4"
						>
							The AI Agentic eCommerce Agency
						</motion.h1>
						<motion.p 
							initial={{ y: 20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.4 }}
							className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-6"
						>
							We build and deploy AI-powered digital workforces that automate tasks, analyse data, and accelerate growth for merchants and agencies.
						</motion.p>
						 <motion.p
							initial={{ y: 20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.5 }}
							className="text-sm text-gray-400 italic mb-8">
							Powered by VortexIQ's class-leading AI Agent Builder.
						</motion.p>
						<motion.div 
							initial={{ y: 20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.6 }}
							className="flex flex-col sm:flex-row gap-4 justify-center"
						>
							<button onClick={() => setPage('Contact')} className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
								Schedule Your AI Strategy Call
							</button>
							<button onClick={() => setPage('AI Workforce')} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
								Explore the AI Workforce
							</button>
						</motion.div>
					</div>
				</div>
			</div>

			{/* Section 2: The End of Manual Work */}
			<section className="py-20 bg-gray-800 text-white">
				<div className="container mx-auto px-6 text-center">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">The eCommerce landscape has changed. Your workforce should too.</h2>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto">
						Repetitive tasks, manual data analysis, and reactive problem-solving are holding you back. We transform your operations by deploying autonomous AI agents that work 24/7, freeing your human talent to focus on what matters most: strategy, innovation, and customer relationships.
					</p>
				</div>
			</section>

			{/* Section 3: Meet Your Digital Workforce */}
			<section className="py-20 bg-gray-900 text-white">
				<div className="container mx-auto px-6">
					<h2 className="text-3xl md:text-4xl font-bold text-center mb-12">A Glimpse Into Our AI Agent Workforce</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						{[{ title: "Marketing & SEO Agents", desc: "Automate keyword research, generate product descriptions, and optimize ad spend." }, { title: "Operations & Logistics Agents", desc: "Manage inventory, forecast demand, and streamline supply chain communication." }, { title: "Platform & Development Agents", desc: "Monitor site performance, identify bugs, and manage complex platform upgrades." }, { title: "Customer Experience Agents", desc: "Analyse support tickets, segment customers, and personalize user journeys." }].map((agent, index) => (
							<motion.div 
								key={agent.title}
								className="bg-gray-800 rounded-lg p-6 transform hover:-translate-y-2 transition duration-300 shadow-xl"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: index * 0.1 }}
								viewport={{ once: true }}
							>
								<h3 className="text-xl font-bold mb-2 text-purple-400">{agent.title}</h3>
								<p className="text-gray-300">{agent.desc}</p>
							</motion.div>
						))}
					</div>
					<div className="text-center mt-12">
						 <button onClick={() => setPage('AI Workforce')} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
							Discover All Agents
						</button>
					</div>
				</div>
			</section>
			
			{/* Section 4: Data-Driven Proof */}
			<section className="py-20 bg-gray-800 text-white">
				<div className="container mx-auto px-6">
					<div className="text-center">
						<h2 className="text-3xl md:text-4xl font-bold mb-4">The Unstoppable Rise of the Digital Workforce</h2>
						<p className="text-lg text-gray-300 max-w-3xl mx-auto mb-12">
							The shift from manual process to AI-driven autonomy is not a trend; it's the new baseline for competitive advantage. The data tells the story.
						</p>
					</div>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div className="bg-gray-900 p-6 rounded-lg shadow-xl">
							<h3 className="text-xl font-bold text-center mb-4">Global AI Agent Deployments (2024-2030)</h3>
							<ResponsiveContainer width="100%" height={300}>
								<LineChart data={deploymentData}>
									<CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
									<XAxis dataKey="name" stroke="white" />
									<YAxis stroke="white" />
									<Tooltip contentStyle={{ backgroundColor: '#2d3748', border: 'none' }}/>
									<Legend />
									<Line type="monotone" dataKey="agents" stroke="#8884d8" strokeWidth={2} activeDot={{ r: 8 }} />
								</LineChart>
							</ResponsiveContainer>
						</div>
						<div className="bg-gray-900 p-6 rounded-lg shadow-xl">
							<h3 className="text-xl font-bold text-center mb-4">Automating the eCommerce Engine</h3>
							<ResponsiveContainer width="100%" height={300}>
								<PieChart>
									<Pie data={automationData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label>
										{automationData.map((entry, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
									</Pie>
									<Tooltip contentStyle={{ backgroundColor: '#2d3748', border: 'none' }}/>
									<Legend />
								</PieChart>
							</ResponsiveContainer>
						</div>
					</div>
				</div>
			</section>
			
			 {/* Section 5: Trusted By Industry Leaders */}
			<section className="py-20 bg-gray-900 text-white">
				<div className="container mx-auto px-6">
					<h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Backed by a World-Class Partner Ecosystem</h2>
					<div className="relative overflow-hidden group">
						<div className="flex animate-scroll group-hover:pause">
							{[...partners, ...partners].map((partner, index) => (
								<div key={index} className="flex-shrink-0 mx-8">
									<img src={partner} alt={`Partner ${index + 1}`} className="h-12 object-contain" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/150x50/111827/ffffff?text=Error'; }} />
								</div>
							))}
						</div>
					</div>
					<div className="mt-16 max-w-3xl mx-auto text-center bg-gray-800 p-8 rounded-lg shadow-xl">
						<p className="text-2xl italic text-gray-300">"Agentic-247's AI workforce revolutionised our promotions strategy, increasing ROI by 35% in one quarter."</p>
						<p className="mt-4 font-bold text-purple-400">- CEO, A Leading Fashion Retailer</p>
						<div className="text-center mt-8">
						   <button onClick={() => setPage('Case Studies')} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
								Read Our Case Studies
							</button>
						</div>
					</div>
				</div>
			</section>
			
			{/* Section 6: Final CTA */}
			<section className="py-20 bg-gradient-to-r from-purple-600 to-cyan-600 text-white">
				<div className="container mx-auto px-6 text-center">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Build Your Future?</h2>
					<p className="text-lg max-w-2xl mx-auto mb-8">
						Stop managing tasks. Start directing strategy. Let's design an AI workforce that gives you an unbeatable competitive edge.
					</p>
					<button onClick={() => setPage('Contact')} className="bg-white text-purple-600 font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-2xl">
						Schedule Your AI Strategy Call
					</button>
				</div>
			</section>
		</motion.div>
	);
};

const About = ({ setPage }) => {
	const journey = [
		{ year: "2011", title: "The Beginning", desc: "Founded as an Amazon Webstores specialist, mastering the world's largest marketplace." },
		{ year: "2013", title: "Embracing Magento", desc: "Aligned with the rise of Magento 1, becoming a key partner for ambitious merchants." },
		{ year: "2015", title: "Pioneering Integration", desc: "Launched Cloudhub, our pioneering integration platform, to solve complex data challenges." },
		{ year: "2018", title: "Enterprise Evolution", desc: "Evolved into a leading Adobe Commerce (Magento 2) agency, delivering enterprise-grade solutions." },
		{ year: "2024", title: "The Agentic Shift", desc: "Transformed into Agentic-247, pivoting our deep eCommerce expertise to build the future: AI-powered digital workforces." },
	];

	const principles = [
		{ title: 'Vision', content: 'To create a world where human ingenuity is amplified by intelligent automation, enabling every eCommerce business to achieve its maximum potential.'},
		{ title: 'Mission', content: 'To design, build, and manage bespoke AI digital workforces that drive efficiency, unlock insights, and create unprecedented growth for our clients.'},
		{ title: 'Aim', content: 'To be the world\'s most trusted AI Agentic eCommerce Consultancy, delivering transformative results through a partnership of strategic expertise and technological excellence.'},
	];

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24">
			{/* Hero */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center">
					<h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400 mb-4">From eCommerce Pioneers to AI Agentic Leaders</h1>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto">For over a decade, we've been at the forefront of digital commerce. Our evolution is driven by a single-minded obsession: finding the most powerful way to grow our clients' businesses. Today, that power is in AI agency.</p>
				</div>
			</section>
			
			{/* Journey */}
			<section className="py-20">
				<div className="container mx-auto px-6">
					<h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Our Journey</h2>
					<div className="relative">
						<div className="border-l-2 border-purple-500 absolute h-full top-0 left-1/2 -ml-px"></div>
						{journey.map((item, index) => (
							<div key={index} className={`mb-8 flex justify-between items-center w-full ${index % 2 === 0 ? 'flex-row-reverse' : ''}`}>
								<div className="order-1 w-5/12"></div>
								<div className="z-20 flex items-center order-1 bg-purple-500 shadow-xl w-8 h-8 rounded-full">
									<h1 className="mx-auto font-semibold text-lg text-white">{index + 1}</h1>
								</div>
								<div className="order-1 bg-gray-800 rounded-lg shadow-xl w-5/12 px-6 py-4">
									<h3 className="mb-3 font-bold text-white text-xl">{item.year} - {item.title}</h3>
									<p className="text-sm leading-snug tracking-wide text-gray-300 text-opacity-100">{item.desc}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			 {/* Guiding Principles */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6">
					<div className="grid md:grid-cols-3 gap-8 text-center">
						{principles.map(p => (
							<div key={p.title}>
								<h3 className="text-2xl font-bold text-purple-400 mb-2">{p.title}</h3>
								<p className="text-gray-300">{p.content}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Leadership */}
			<section className="py-20">
				<div className="container mx-auto px-6">
					<h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Meet the Leadership</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
						<div className="bg-gray-800 p-6 rounded-lg text-center shadow-xl">
							<img src="https://placehold.co/150x150/805ad5/ffffff?text=SP" alt="Susant Patro" className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-purple-400"/>
							<h3 className="text-xl font-bold">Susant Patro</h3>
							<p className="text-purple-400 mb-2">Founder & Chief Visionary</p>
							<p className="text-gray-400">Susant is the architect of our evolution. With a deep legacy in eCommerce and as the founder of both 247 Commerce and the VortexIQ AI platform, he provides the visionary leadership that connects our profound industry knowledge with the limitless possibilities of artificial intelligence.</p>
						</div>
						 <div className="bg-gray-800 p-6 rounded-lg text-center shadow-xl">
							<img src="https://placehold.co/150x150/38b2ac/ffffff?text=BG" alt="Benjamin Gisvold" className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-cyan-400"/>
							<h3 className="text-xl font-bold">Benjamin Gisvold</h3>
							<p className="text-cyan-400 mb-2">CEO & Lead Agentic Strategist</p>
							<p className="text-gray-400">Benjamin drives the strategic application of our AI technology. With a focus on client success and operational excellence, he leads the consultancy, ensuring that every digital workforce we deploy is perfectly aligned with solving our clients' most critical commercial challenges.</p>
						</div>
					</div>
				</div>
			</section>
			
			{/* How We Work */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6">
					<h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Your Agentic Transformation</h2>
					<div className="grid md:grid-cols-3 gap-8 text-center">
					   {[{title: "Strategic Consulting", desc: "We begin with a deep-dive immersion into your business. We learn your goals, pain points, processes, and data ecosystems. This is a partnership, not a sales pitch."}, {title: "Workforce Design & Build", desc: "Using the insights from the consultancy phase, our strategists and engineers design your bespoke AI workforce on the VortexIQ platform. We build, test, and refine each agent for optimal performance."}, {title: "Deployment & Scale", desc: "We deploy the agents into your live environment with robust monitoring and guardrails. As your business grows, we work with you to scale your digital workforce, adding new capabilities to meet emerging challenges."}].map((step, i) =>(
						   <div key={i} className="p-6">
								<div className="text-5xl font-extrabold text-purple-400 mb-4">0{i+1}</div>
								<h3 className="text-xl font-bold mb-2">{step.title}</h3>
								<p className="text-gray-400">{step.desc}</p>
						   </div>
					   ))}
					</div>
					 <div className="text-center mt-12">
						 <button onClick={() => setPage('Contact')} className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
							Start Your Consultation
						</button>
					</div>
				</div>
			</section>
		</motion.div>
	);
};

const AiWorkforce = ({ setPage }) => {
	const agents = {
		'Platform Management': [
			{ name: 'Adobe Commerce Upgrade Agent', description: 'Autonomously analyses your instance, identifies breaking changes in a new version, and outlines a precise upgrade path.' },
			{ name: 'Performance Monitoring Agent', description: 'Runs 24/7 site audits, flagging Core Web Vitals issues, broken links, and performance degradation in real-time.' },
		],
		'Marketing & Merchandising': [
			{ name: 'SEO Agent', description: 'Conducts continuous keyword research, monitors SERP rankings, identifies content gaps, and flags technical SEO issues.' },
			{ name: 'Image Optimiser Agent', description: 'Scans your product catalog, automatically compresses images to the optimal format and size without quality loss.' },
			{ name: 'Promotions Agent', description: 'Analyses sales data to recommend optimal discount levels and automatically schedules promotions in your eCommerce platform.' },
		],
		'Strategy & Operations': [
			{ name: 'Discovery Outline Agent', description: 'Listens to a stakeholder meeting recording and automatically generates a structured project discovery document, including requirements and key discussion points.' },
			{ name: 'Inventory Forecast Agent', description: 'Analyses sales velocity and supply chain data to predict stock-outs and recommend purchase orders.' },
		]
	};

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24">
			 {/* Hero */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center">
					<h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400 mb-4">The Unstoppable Rise of the Digital Workforce</h1>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto">Our agents are more than tools; they are tireless, intelligent team members trained for eCommerce excellence.</p>
				</div>
			</section>

			 {/* Agent Library */}
			<section className="py-20">
				<div className="container mx-auto px-6">
					<div className="text-center">
						<h2 className="text-3xl md:text-4xl font-bold mb-4">Explore Our Pre-Trained & Customisable Agents</h2>
						<p className="text-lg text-gray-300 max-w-3xl mx-auto mb-12">Start with our extensive library of agents trained on core eCommerce tasks, or work with us to build custom agents tailored to your unique operational needs.</p>
					</div>

					{Object.entries(agents).map(([category, agentList]) => (
						<div key={category} className="mb-16">
							<h3 className="text-2xl font-bold text-purple-400 mb-6">{category}</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
								{agentList.map((agent) => (
									<div key={agent.name} className="bg-gray-800 p-6 rounded-lg shadow-xl flex flex-col justify-between transform hover:-translate-y-2 transition duration-300">
										<div>
											<h4 className="text-xl font-bold mb-2">{agent.name}</h4>
											<p className="text-gray-400 mb-4">{agent.description}</p>
										</div>
										<button className="mt-auto bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-300 w-full">
											See Agent in Action
										</button>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</section>
			 {/* Final CTA */}
			<section className="py-20 bg-gradient-to-r from-purple-600 to-cyan-600 text-white">
				<div className="container mx-auto px-6 text-center">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">What Could Your Digital Workforce Achieve?</h2>
					<button onClick={() => setPage('Contact')} className="mt-4 bg-white text-purple-600 font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-2xl">
						Design Your Workforce
					</button>
				</div>
			</section>
		</motion.div>
	);
};

const Partners = ({ setPage }) => {
	const techPartners = ["Mollie", "BarclayCard", "Dotdigital", "Mailchimp", "Adobe Commerce", "Shopify", "BigCommerce", "Shopline", "Gorgias", "Athos Commerce", "Yotpo", "BazaarVoice"];

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24">
			{/* Hero */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center">
					<h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400 mb-4">Excellence is a Team Sport</h1>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto">Our technology and agency partnerships ensure that your AI workforce integrates seamlessly with the tools you already use and trust, creating a unified and powerful commerce ecosystem.</p>
				</div>
			</section>

			{/* Vortex IQ Spotlight */}
			<section className="py-20">
				<div className="container mx-auto px-6">
					<div className="bg-gray-800 rounded-lg shadow-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-8 border-t-4 border-purple-500">
						<div className="flex-shrink-0">
							<img src="https://placehold.co/200x200/1F2937/FFFFFF?text=VortexIQ" alt="Vortex IQ" className="rounded-lg" />
						</div>
						<div>
							<h2 className="text-3xl font-bold mb-4 flex items-center"><Icons.zap className="text-purple-400 w-8 h-8 mr-3" /> Our Core AI Partner</h2>
							<p className="text-lg text-gray-300 mb-4">
								Agentic-247's entire suite of AI agents is powered by <span className="font-bold text-white">VortexIQ</span>, the class-leading AI Agent Builder platform. This strategic partnership gives us an unparalleled ability to create, deploy, and manage sophisticated AI workforces that are robust, scalable, and secure. VortexIQ provides the intelligent engine; we provide the strategic eCommerce expertise.
							</p>
							<a href="#" className="font-bold text-cyan-400 hover:text-cyan-300 transition duration-300">Learn more about the VortexIQ Platform &rarr;</a>
						</div>
					</div>
				</div>
			</section>

			{/* Other Tech Partners */}
			<section className="py-20 bg-gray-900">
				<div className="container mx-auto px-6">
					<h2 className="text-3xl font-bold text-center mb-12">The Platforms That Power Your Growth</h2>
					<div className="flex flex-wrap justify-center items-center gap-8">
						{techPartners.map(name => (
							<div key={name} className="p-4 bg-gray-800 rounded-lg">
								<img src={`https://placehold.co/180x60/1F2937/a78bfa?text=${name.replace(' ', '+')}`} alt={name} className="h-10 object-contain" onError={(e) => e.target.src = 'https://placehold.co/180x60/1F2937/a78bfa?text=Logo'}/>
							</div>
						))}
					</div>
				</div>
			</section>

			 {/* Agency Partners */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center max-w-4xl">
					<h2 className="text-3xl font-bold mb-4">Collaborating to Elevate the Industry</h2>
					<p className="text-lg text-gray-300 mb-8">We partner with leading digital, marketing, and creative agencies who want to bring the power of AI agency to their clients. We provide the AI expertise, and you provide the client strategy, creating a winning combination.</p>
					 <button onClick={() => setPage('Contact')} className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
						Learn About Agency Partnerships
					</button>
				</div>
			</section>
		</motion.div>
	);
};

const CaseStudies = () => {
	const studies = [
		{ client: 'A Fashion Retailer', title: 'Automating SEO Content Production to Triple Organic Traffic', metrics: ['+210% Organic Traffic', '50+ hours saved per month', '#1 Ranking for 15 core keywords'], logo: 'https://placehold.co/100x100/111827/ffffff?text=Fashion' },
		{ client: 'A B2B Parts Supplier', title: 'AI-Powered Adobe Commerce Upgrade Reduces Project Time by 40%', metrics: ['40% faster upgrade cycle', '99% reduction in post-launch critical bugs', 'Zero operational downtime'], logo: 'https://placehold.co/100x100/111827/ffffff?text=B2B' },
		{ client: 'Global CPG Brand', title: 'Inventory Forecasting Agent Cuts Stock-outs by 75%', metrics: ['75% fewer stock-outs', '15% reduction in holding costs', 'Automated purchase ordering'], logo: 'https://placehold.co/100x100/111827/ffffff?text=CPG' }
	];

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24">
			{/* Hero */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center">
					<h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400 mb-4">Real Results, Powered by AI</h1>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto">Explore how we've deployed autonomous AI workforces to solve complex challenges and drive measurable growth for businesses like yours.</p>
				</div>
			</section>

			 {/* Success Stories */}
			<section className="py-20">
				<div className="container mx-auto px-6">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{studies.map((study, index) => (
							<motion.div 
								key={index} 
								className="bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col transform hover:-translate-y-2 transition duration-300"
								initial={{ opacity: 0, y: 50 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: index * 0.1 }}
								viewport={{ once: true }}
							>
								<div className="p-6">
									<img src={study.logo} alt={`${study.client} logo`} className="h-16 w-16 mb-4 rounded-full" onError={(e) => e.target.src = 'https://placehold.co/100x100/111827/ffffff?text=Logo'} />
									<h2 className="text-2xl font-bold mb-2">{study.title}</h2>
									<p className="text-sm text-purple-400 font-bold mb-4">{study.client}</p>
								</div>
								<div className="bg-gray-700 p-6 flex-grow">
									<ul className="space-y-2">
										{study.metrics.map((metric, i) => (
											<li key={i} className="flex items-start">
												<Icons.check className="text-cyan-400 w-5 h-5 mr-2 mt-1 flex-shrink-0" />
												<span>{metric}</span>
											</li>
										))}
									</ul>
								</div>
								<div className="p-6 bg-gray-800">
									<button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300">Read Full Case Study</button>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>
		</motion.div>
	);
};

const Trust = () => {
	const frameworkPoints = [
	  { title: "Human-in-the-Loop", description: "Critical decisions can always be configured to require human approval. You are always in control." },
	  { title: "Explainability (XAI)", description: "Our agents provide clear logs and reasoning for their actions. You'll know not just what they did, but why." },
	  { title: "Bias Mitigation", description: "We actively work to identify and eliminate bias in the data and models our agents use." },
	  { title: "Data Privacy", description: "Your data is yours. Our agents operate within strict privacy protocols, ensuring sensitive information is never compromised." }
	];

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24">
			{/* Hero */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center">
					<h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400 mb-4">Innovation with Integrity</h1>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto">The power of autonomous AI demands a profound commitment to safety, ethics, and transparency. Our responsible AI framework is not an afterthought; it is core to our architecture and our promise to you.</p>
				</div>
			</section>

			 {/* Framework */}
			<section className="py-20">
				<div className="container mx-auto px-6">
					<h2 className="text-3xl font-bold text-center mb-12">Building Guardrails for a Digital Workforce</h2>
					<div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
						{frameworkPoints.map(point => (
							 <div key={point.title} className="bg-gray-800 p-6 rounded-lg shadow-lg">
								<Icons.shield className="text-purple-400 w-8 h-8 mb-4" />
								<h3 className="text-xl font-bold mb-2">{point.title}</h3>
								<p className="text-gray-400">{point.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>
			
			{/* Certifications */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center">
					<h2 className="text-3xl font-bold mb-4">Independently Audited and Certified</h2>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto mb-12">Our commitment to quality, security, and responsible innovation is verified by leading international standards.</p>
					<div className="flex flex-wrap justify-center items-center gap-8">
						<div className="text-center p-4">
							<img src="https://placehold.co/120x120/1F2937/a78bfa?text=ISO+27001" alt="ISO 27001" className="h-24 mx-auto mb-2"/>
							<p className="font-bold">Information Security</p>
						</div>
						 <div className="text-center p-4">
							<img src="https://placehold.co/120x120/1F2937/a78bfa?text=ISO+14001" alt="ISO 14001" className="h-24 mx-auto mb-2"/>
							<p className="font-bold">Environmental Management</p>
						</div>
						 <div className="text-center p-4">
							<img src="https://placehold.co/120x120/1F2937/a78bfa?text=ISO+42001" alt="ISO 42001" className="h-24 mx-auto mb-2"/>
							<p className="font-bold">AI Management System</p>
						</div>
					</div>
				</div>
			</section>
		</motion.div>
	);
};

const Pricing = ({ setPage }) => {
	const plans = [
		{ name: 'Lite', price: 'Contact Us', desc: 'For small businesses automating their first key tasks.', features: ['Up to 3 Agents', '10,000 Actions/mo', 'Email Support'] },
		{ name: 'Standard', price: 'Contact Us', desc: 'For growing companies scaling their digital workforce.', features: ['Up to 10 Agents', '50,000 Actions/mo', 'Priority Support'], popular: true },
		{ name: 'Premium', price: 'Contact Us', desc: 'For enterprises requiring advanced security and customisation.', features: ['Unlimited Agents', 'Custom Actions', 'Dedicated Strategist', 'No Agentic-247 Branding'] },
	];

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24">
			{/* Hero */}
			<section className="py-20 bg-gray-800">
				<div className="container mx-auto px-6 text-center">
					<h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400 mb-4">A Plan for Every Ambition</h1>
					<p className="text-lg text-gray-300 max-w-3xl mx-auto">Start with a consultation, scale with confidence. Our pricing is designed to grow with you, based on the value and actions your digital workforce delivers.</p>
				</div>
			</section>

			 {/* Plans */}
			<section className="py-20">
				<div className="container mx-auto px-6">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
						{/* Consultancy Card */}
						<div className="bg-gray-800 rounded-lg shadow-xl p-8 flex flex-col text-center border-2 border-purple-500">
							<h3 className="text-2xl font-bold mb-2">Consultancy</h3>
							 <p className="text-gray-400 flex-grow mb-4">A bespoke strategic engagement to design your AI workforce architecture.</p>
							<p className="text-4xl font-bold my-4">Custom</p>
							<button onClick={() => setPage('Contact')} className="mt-auto w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300">Request a Quote</button>
						</div>

						{/* Tiered Plans */}
						{plans.map(plan => (
							 <div key={plan.name} className={`bg-gray-800 rounded-lg shadow-xl p-8 flex flex-col text-center ${plan.popular ? 'border-2 border-cyan-400 relative' : ''}`}>
								{plan.popular && <span className="absolute top-0 right-0 bg-cyan-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-bl-lg">POPULAR</span>}
								<h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
								<p className="text-gray-400 mb-4 h-16">{plan.desc}</p>
								<p className="text-4xl font-bold my-4">{plan.price}</p>
								<ul className="text-left space-y-2 my-6 flex-grow">
									{plan.features.map(feature => (
										<li key={feature} className="flex items-center"><Icons.check className="text-cyan-400 w-5 h-5 mr-2" />{feature}</li>
									))}
								</ul>
								<button onClick={() => setPage('Contact')} className="mt-auto w-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-bold py-2 px-4 rounded-lg transition duration-300">Get Started</button>
							</div>
						))}
					</div>
					 <div className="text-center text-gray-400 mt-8">
						<p>*An "action" is a discrete task completed by an agent, like optimising one image or generating one report. We'll help you estimate your usage.</p>
						<p>**Enterprise plans available with custom SLAs and features. Contact us.</p>
					 </div>
				</div>
			</section>
		</motion.div>
	);
};

const FAQ = () => {
	const [openIndex, setOpenIndex] = useState(null);
	const faqs = [
		{ q: 'What is an "AI Agentic" consultancy?', a: 'Unlike a traditional agency that provides services, an AI Agentic consultancy designs, builds, and deploys autonomous AI agents that become part of your digital workforce. We focus on automating complex processes and workflows, not just individual tasks.' },
		{ q: "What's the difference between simple automation and an AI Agent?", a: 'Simple automation follows a fixed set of rules (if X, then Y). An AI Agent can perceive its environment, make decisions, learn from outcomes, and take autonomous actions to achieve a goal. They are proactive, not just reactive.' },
		{ q: 'How do you ensure the privacy and security of my company\'s data?', a: 'Security is our top priority. We adhere to strict ISO 27001 and 42001 standards, implement robust data encryption, and ensure our agents operate within secure, isolated environments. Your data is used only for the tasks you authorize and is never used for training external models.' },
		{ q: 'Is this going to replace my team?', a: 'Our goal is augmentation, not replacement. By automating repetitive and data-intensive tasks, our AI agents free up your talented team members to focus on high-value activities like strategy, creativity, and building customer relationships. We empower your team to do more, better.' },
		{ q: 'What is VortexIQ?', a: 'VortexIQ is our proprietary, class-leading AI Agent Builder platform. It provides the core technology, frameworks, and tools that allow us to rapidly design, train, and deploy sophisticated AI agents for the eCommerce industry.' },
		{ q: 'How long does it take to deploy our first agent?', a: 'The timeline depends on the complexity of the task, but after our initial strategic consultation, a pre-trained agent can often be configured and deployed in a matter of days. Custom agents may take a few weeks to build and test thoroughly.' },
	];
	
	const toggleFAQ = index => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24 min-h-screen">
			<div className="container mx-auto px-6 py-20">
				<h1 className="text-4xl font-bold text-center mb-12">Frequently Asked Questions</h1>
				<div className="max-w-3xl mx-auto">
					{faqs.map((faq, index) => (
						<div key={index} className="border-b border-gray-700">
							<button onClick={() => toggleFAQ(index)} className="w-full text-left py-4 flex justify-between items-center">
								<span className="text-lg font-semibold">{faq.q}</span>
								<span className="text-purple-400 transform transition-transform duration-300" style={{ transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0)' }}>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
								</span>
							</button>
							<AnimatePresence>
								{openIndex === index && (
									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: 'auto', opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										className="overflow-hidden"
									>
										<p className="pb-4 text-gray-300">{faq.a}</p>
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					))}
				</div>
			</div>
		</motion.div>
	);
};

const Masterclass = () => {
	const speakers = [
		{ name: 'Susant Patro', title: 'Chief Visionary, Agentic-247', img: 'https://placehold.co/150x150/805ad5/ffffff?text=SP'},
		{ name: 'Benjamin Gisvold', title: 'Lead Agentic Strategist, 247C', img: 'https://placehold.co/150x150/38b2ac/ffffff?text=BG'},
		{ name: 'Guest Speaker', title: 'Senior Engineer, VortexIQ', img: 'https://placehold.co/150x150/gray/ffffff?text=VortexIQ'},
		{ name: 'Guest Speaker', title: 'Head of Innovation, Dotdigital', img: 'https://placehold.co/150x150/gray/ffffff?text=DD'},
	];
	
	 const learningPoints = [
		{ title: 'The Agentic Revolution', description: 'Understand the fundamental shift from basic automation and chatbots to coordinated, autonomous AI agents that can reason and execute complex tasks.' },
		{ title: 'Hands-On Lab: Build Your First Agent', description: 'In a live, guided session using the VortexIQ platform, you will design, train, and deploy a functional eCommerce agent from scratch. No code required.' },
		{ title: 'Designing a Digital Workforce', description: 'Learn the principles of Master-Controller-Performer (MCP) architecture, agent coordination, and how to design complex, multi-agent workflows that solve real-world business problems.' },
		{ title: 'Responsible AI & Guardrails', description: 'Go beyond the buzzwords. Learn how to implement practical ethical frameworks, security protocols, and human-in-the-loop systems for safe, transparent, and autonomous operation.' },
	];

	const handleSubmit = (e) => {
		e.preventDefault();
		alert('Thank you for your interest! We will be in touch with an invitation.');
	};

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-900 text-white pt-24">
			{/* Hero */}
			<section className="py-20 text-center" style={{ background: 'linear-gradient(135deg, #5b21b6, #1d4ed8)' }}>
				<div className="container mx-auto px-6">
					<h1 className="text-4xl md:text-6xl font-extrabold mb-4">Become an Agentic Leader</h1>
					<p className="text-xl text-gray-200 max-w-3xl mx-auto mb-2">Join our inaugural Masterclass and gain the strategic and practical skills to build, manage, and scale a digital workforce.</p>
					<p className="font-bold text-cyan-300">September 2025 | In-Person and Recorded Options Available</p>
				</div>
			</section>
			
			<section className="py-20">
				<div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
					 {/* Form Section */}
					 <div className="bg-gray-800 p-8 rounded-lg shadow-2xl sticky top-28">
						<h2 className="text-3xl font-bold text-center mb-6">Reserve Your Spot</h2>
						<form onSubmit={handleSubmit}>
							<div className="mb-4">
								<label htmlFor="fullName" className="block text-gray-300 mb-2">Full Name</label>
								<input type="text" id="fullName" className="w-full bg-gray-700 text-white rounded-lg p-3 border border-gray-600 focus:ring-2 focus:ring-purple-500 focus:outline-none" required />
							</div>
							<div className="mb-4">
								<label htmlFor="email" className="block text-gray-300 mb-2">Email Address</label>
								<input type="email" id="email" className="w-full bg-gray-700 text-white rounded-lg p-3 border border-gray-600 focus:ring-2 focus:ring-purple-500 focus:outline-none" required />
							</div>
							<div className="mb-6">
								<label htmlFor="company" className="block text-gray-300 mb-2">Company Name</label>
								<input type="text" id="company" className="w-full bg-gray-700 text-white rounded-lg p-3 border border-gray-600 focus:ring-2 focus:ring-purple-500 focus:outline-none" required />
							</div>
							<button type="submit" className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
								Request Invite
							</button>
						</form>
					</div>

					{/* Content Section */}
					<div>
						<div className="mb-12">
							<h2 className="text-3xl font-bold text-cyan-400 mb-4">The Agentic Shift Is Here. Lead It, or Be Left Behind.</h2>
							<p className="text-gray-300 text-lg">This isn't another webinar on AI theory. This is a hands-on, strategic masterclass for eCommerce leaders, founders, and agency heads. You will leave with a functional agent and a clear roadmap for transforming your business.</p>
						</div>
						
						<div className="mb-12">
							<h3 className="text-2xl font-bold mb-6">What You'll Learn</h3>
							<div className="space-y-6">
								{learningPoints.map((point, i) => (
									<div key={point.title} className="flex items-start">
										<div className="text-2xl font-bold text-purple-400 mr-4">M{i+1}</div>
										<div>
											<h4 className="font-bold text-lg">{point.title}</h4>
											<p className="text-gray-400">{point.description}</p>
										</div>
									</div>
								))}
							</div>
						</div>

						<div>
							<h3 className="text-2xl font-bold mb-6">Meet The Speakers</h3>
							<div className="grid grid-cols-2 gap-6">
								{speakers.map(speaker => (
									<div key={speaker.name} className="text-center">
										<img src={speaker.img} alt={speaker.name} className="w-24 h-24 rounded-full mx-auto mb-2 border-2 border-gray-600" />
										<h4 className="font-bold">{speaker.name}</h4>
										<p className="text-sm text-gray-400">{speaker.title}</p>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</section>
		</motion.div>
	);
};

// --- GLOBAL COMPONENTS ---

const Header = ({ page, setPage }) => {
	const [isOpen, setIsOpen] = useState(false);
	const navItems = ['Home', 'About', 'AI Workforce', 'Partners', 'Case Studies', 'Trust', 'Pricing', 'Masterclass'];

	const handleNavClick = (newPage) => {
		setPage(newPage);
		setIsOpen(false);
		window.scrollTo(0, 0);
	};

	return (
		<header className="bg-gray-900/80 backdrop-blur-sm text-white fixed top-12 w-full z-40 shadow-lg">
			<div className="container mx-auto px-6 flex justify-between items-center h-16">
				<a href="#" onClick={() => handleNavClick('Home')} className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-cyan-400">Agentic-247</a>
				
				{/* Desktop Nav */}
				<nav className="hidden lg:flex items-center space-x-6">
					{navItems.map(item => (
						<a key={item} href="#" onClick={() => handleNavClick(item)} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${page === item ? 'bg-purple-600' : 'hover:bg-gray-700'}`}>
							{item === 'AI Workforce' ? 'AI Workforce' : item}
						</a>
					))}
				</nav>

				<div className="hidden lg:block">
					 <button onClick={() => handleNavClick('Contact')} className="bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-300 text-sm">
						Schedule a Call
					</button>
				</div>
				
				{/* Mobile Nav Toggle */}
				<div className="lg:hidden">
					<button onClick={() => setIsOpen(!isOpen)}>
						{isOpen ? <Icons.x /> : <Icons.menu />}
					</button>
				</div>
			</div>
			
			 {/* Mobile Nav Menu */}
			 <AnimatePresence>
				{isOpen && (
					<motion.div 
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						className="lg:hidden bg-gray-900"
					>
						<nav className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
							 {navItems.map(item => (
								<a key={item} href="#" onClick={() => handleNavClick(item)} className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${page === item ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}>
									{item}
								</a>
							))}
						</nav>
						<div className="p-4">
							<button onClick={() => handleNavClick('Contact')} className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-300 text-sm">
								Schedule a Call
							</button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</header>
	);
};

const MasterclassBanner = ({ setPage }) => (
	<div className="bg-gradient-to-r from-purple-600 to-cyan-500 text-white p-3 text-center text-sm fixed top-0 w-full z-50">
		<div className="container mx-auto flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
			<span className="font-semibold">Become an Agentic Leader:</span>
			<span className="hidden sm:inline">Join our inaugural Masterclass to build, manage, and scale a digital workforce.</span>
			<button onClick={() => setPage('Masterclass')} className="bg-white text-purple-600 font-bold py-1 px-3 rounded-full text-xs transition duration-300 transform hover:scale-105 shadow-md">
				Request Invite
			</button>
		</div>
	</div>
);


const ChatWidget = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState([]);
	const [userInput, setUserInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const messagesEndRef = useRef(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(scrollToBottom, [messages]);
	
	// Proactive greeting
	useEffect(() => {
		const timer = setTimeout(() => {
			if(!isOpen && messages.length === 0){
				setIsOpen(true);
				setMessages([{
					role: 'assistant',
					text: "Hi there. I'm an AI assistant. How can I help you accelerate your eCommerce growth today? Feel free to ask me about AI strategy, our agents, or even for a quick UX review of your site."
				}]);
			}
		}, 10000); // 10 seconds
		return () => clearTimeout(timer);
	}, [isOpen, messages.length]);

	const handleSend = async () => {
		if (!userInput.trim()) return;

		const newMessages = [...messages, { role: 'user', text: userInput }];
		setMessages(newMessages);
		setUserInput('');
		setIsLoading(true);

		const prompt = `You are a helpful AI assistant for Agentic-247, an AI Agentic eCommerce Consultancy. Your goal is to be helpful, answer questions about the company and its services, provide e-commerce ideas, and ultimately guide users towards booking a consultation.

		Previous conversation:
		${messages.map(m => `${m.role}: ${m.text}`).join('\n')}

		User's new message: ${userInput}

		Your response should be helpful and encouraging. If the user seems interested in our services, gently nudge them towards booking a call. For example, you can end your response with something like: 'Would you be interested in scheduling a free AI strategy call with one of our experts?'
		`;

		try {
			// This is a mock API call for demonstration.
			// In a real application, you would replace this with a fetch call to the Gemini API.
			const chatHistory = [{ role: "user", parts: [{ text: prompt }] }];
			const payload = { contents: chatHistory };
			const apiKey = ""; // Left empty for Canvas environment
			const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload)
			});
			
			if(!response.ok) {
				throw new Error(`API call failed with status: ${response.status}`);
			}

			const result = await response.json();
			
			let botResponse = "I'm having a little trouble connecting right now. Please try again later.";
			if (result.candidates && result.candidates.length > 0 && result.candidates[0].content && result.candidates[0].content.parts && result.candidates[0].content.parts.length > 0) {
				 botResponse = result.candidates[0].content.parts[0].text;
			}
			
			setMessages(prev => [...prev, { role: 'assistant', text: botResponse }]);

		} catch (error) {
			console.error('Error fetching from Gemini API:', error);
			setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I encountered an error. Could you please rephrase your question?' }]);
		} finally {
			setIsLoading(false);
		}
	};


	return (
		<div className="fixed bottom-5 right-5 z-50">
			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{ opacity: 0, y: 20, scale: 0.9 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 20, scale: 0.9 }}
						className="w-80 h-[28rem] bg-gray-800 rounded-lg shadow-2xl flex flex-col"
					>
						<div className="bg-gray-900 p-3 flex justify-between items-center rounded-t-lg">
							<h3 className="text-white font-bold">Agentic-247 Assistant</h3>
							<button onClick={() => setIsOpen(false)} className="text-white">
								<Icons.x className="w-5 h-5" />
							</button>
						</div>
						<div className="flex-1 p-4 overflow-y-auto">
							{messages.map((msg, index) => (
								<div key={index} className={`mb-3 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
									<div className={`rounded-lg px-3 py-2 max-w-xs ${msg.role === 'user' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-white'}`}>
										{msg.text}
									</div>
								</div>
							))}
							{isLoading && (
								<div className="flex justify-start">
									<div className="rounded-lg px-3 py-2 bg-gray-700 text-white">
										<div className="flex items-center space-x-1">
											<div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
											<div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse [animation-delay:0.2s]"></div>
											<div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse [animation-delay:0.4s]"></div>
										</div>
									</div>
								</div>
							)}
							<div ref={messagesEndRef} />
						</div>
						<div className="p-3 border-t border-gray-700 flex">
							<input
								type="text"
								value={userInput}
								onChange={(e) => setUserInput(e.target.value)}
								onKeyPress={(e) => e.key === 'Enter' && handleSend()}
								placeholder="Ask me anything..."
								className="flex-1 bg-gray-700 text-white rounded-l-lg p-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
								disabled={isLoading}
							/>
							<button onClick={handleSend} disabled={isLoading} className="bg-purple-600 text-white p-2 rounded-r-lg hover:bg-purple-700 disabled:bg-gray-500">
								<Icons.send className="w-5 h-5" />
							</button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
			{!isOpen && (
				<motion.button
					initial={{ scale: 0 }}
					animate={{ scale: 1 }}
					onClick={() => setIsOpen(true)}
					className="bg-gradient-to-r from-purple-500 to-cyan-500 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-2xl"
				>
					<Icons.messageSquare className="w-8 h-8" />
				</motion.button>
			)}
		</div>
	);
};


const Footer = ({ setPage }) => {
	return (
		<footer className="bg-gray-900 text-white border-t border-gray-800">
			<div className="container mx-auto px-6 py-12">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
					{/* Column 1: Address */}
					<div>
						<h3 className="text-xl font-bold mb-4">Agentic-247</h3>
						<address className="text-gray-400 not-italic space-y-1">
							<p>247 Commerce</p>
							<p>International House</p>
							<p>123 Technology Road</p>
							<p>London, W1A 1AA</p>
							<p>United Kingdom</p>
						</address>
						<div className="flex space-x-4 mt-4">
							{['https://placehold.co/32x32/1F2937/a78bfa?text=LI', 'https://placehold.co/32x32/1F2937/a78bfa?text=X'].map(src => <a key={src} href="#"><img src={src} className="rounded-full" alt="Social media icon" /></a>)}
						</div>
					</div>
					{/* Column 2: Quick Links */}
					<div>
						<h4 className="font-bold mb-4">Quick Links</h4>
						<ul className="space-y-2">
							 {['Home', 'About', 'AI Workforce', 'Pricing'].map(item => (
								<li key={item}><a href="#" onClick={() => setPage(item)} className="text-gray-400 hover:text-white transition">{item}</a></li>
							 ))}
						</ul>
					</div>
					{/* Column 3: Resources */}
					<div>
						<h4 className="font-bold mb-4">Resources</h4>
						<ul className="space-y-2">
							 {['Case Studies', 'Trust', 'FAQ', 'Masterclass'].map(item => (
								<li key={item}><a href="#" onClick={() => setPage(item)} className="text-gray-400 hover:text-white transition">{item}</a></li>
							 ))}
						</ul>
					</div>
					{/* Column 4: Certifications */}
					<div>
						<h4 className="font-bold mb-4">Trust & Certification</h4>
						 <div className="flex items-center space-x-4">
							<img src="https://placehold.co/40x40/1F2937/a78bfa?text=27" title="ISO 27001" alt="ISO 27001" className="rounded"/>
							<img src="https://placehold.co/40x40/1F2937/a78bfa?text=14" title="ISO 14001" alt="ISO 14001" className="rounded"/>
							<img src="https://placehold.co/40x40/1F2937/a78bfa?text=42" title="ISO 42001" alt="ISO 42001" className="rounded"/>
						</div>
					</div>
				</div>
				<div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
					<p>&copy; 2025 247 Commerce. All rights reserved.</p>
				</div>
			</div>
		</footer>
	);
};


// --- MAIN APP ---
export default function App() {
	const [page, setPage] = useState('Home');

	const renderPage = () => {
		switch (page) {
			case 'Home': return <Home setPage={setPage}/>;
			case 'About': return <About setPage={setPage}/>;
			case 'AI Workforce': return <AiWorkforce setPage={setPage}/>;
			case 'Partners': return <Partners setPage={setPage}/>;
			case 'Case Studies': return <CaseStudies />;
			case 'Trust': return <Trust />;
			case 'Pricing': return <Pricing setPage={setPage}/>;
			case 'FAQ': return <FAQ />;
			case 'Masterclass': return <Masterclass />;
			// A simple contact page for demonstration
			case 'Contact': return (
				<div className="bg-gray-900 text-white pt-24 min-h-screen flex items-center justify-center">
					<div className="text-center">
						<h1 className="text-4xl font-bold mb-4">Schedule a Call</h1>
						<p className="text-lg text-gray-300">Our calendar integration is coming soon. For now, please reach out via our AI assistant!</p>
					</div>
				</div>
			);
			default: return <Home setPage={setPage}/>;
		}
	};

	return (
		<div className="bg-gray-900">
			<style jsx global>{`
			  @keyframes scroll {
				  0% { transform: translateX(0); }
				  100% { transform: translateX(-50%); }
			  }
			  .animate-scroll {
				  animation: scroll 40s linear infinite;
			  }
			  .group:hover .animate-scroll {
				  animation-play-state: paused;
			  }
			`}</style>
			<MasterclassBanner setPage={setPage} />
			<Header page={page} setPage={setPage} />
			<main>
				<AnimatePresence mode="wait">
					<motion.div
						key={page}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
					>
						 {renderPage()}
					</motion.div>
				</AnimatePresence>
			</main>
			<Footer setPage={setPage} />
			<ChatWidget />
		</div>
	);
}
